import { ICategory } from '@collections/categories/interfaces/category.interface';
import { UsersService } from '@shared/services/users/users.service';
import { Inject, Injectable, LOCALE_ID, Optional, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Subject, BehaviorSubject, Observable, fromEvent, from, ReplaySubject } from 'rxjs';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, NavigationEnd, NavigationStart, Scroll, ActivationEnd, RoutesRecognized, RouteConfigLoadStart } from '@angular/router';
import { filter, map, startWith, mergeMap, takeUntil, scan, take, tap, catchError } from 'rxjs/operators';
import { CompanyBySlugGQL } from '@shared/services/company/companyBySlug.gql.service';
import { ICompany } from '@collections/companies/interfaces/company.interface';
import { StandLayoutEnum } from './stand.enums';
import { Platform } from '@angular/cdk/platform';
import { RoutesService } from '@shared/services/routes/route.service';
import { LanguagesService } from '@shared/services/languages/languages.service';
import { IPathLocale } from '@collections/common/interfaces/path-locale.interface';
import { BreadcrumbsService, IBreadcrumb } from '@shared/services/breadcrumbs/breadcrumbs.service';
import { MetadataService } from '@shared/services/meta-data-service/meta-data.service';
import { ErrorService } from 'app/error-handler/services/error.service';
import { PageNotFoundError } from 'app/error-handler/custom-errors/page-not-found.error';
import { IsoCountriesService } from 'app/shared/services/iso-countries/iso-countries.service';
import { UserRolesEnum } from '@collections/users/enums/user-roles.enums';
import { RobotsIndexEnum } from 'app/shared/services/meta-data-service/meta-data.commons';
import { AccountType } from '@collections/companies/enums';

export type NavigationTrigger = 'imperative' | 'popstate' | 'hashchange';

export interface HistoryEntry {
  id: number;
  url: string;
}

export interface RouterHistory {
  history: HistoryEntry[];
  currentIndex: number;

  event: NavigationStart | NavigationEnd;
  trigger: NavigationTrigger;
  id: number;
  idToRestore: number;
}

export interface IStandLayoutOptions {
  collapse?: boolean;
  animate?: boolean;
  hideNavBar: boolean;
  showBackBtn?: boolean;
  lockCollapsed?: boolean;
  customH1Tag?: boolean;
}
interface IStandComponent {
  navigationItem: {
    label: string;
    path: string;
    icon: string;
  };
}

export interface IStandPage {
  label: string;
  path: string;
  icon: string;
  rel: string;
}


interface IStandConfiguration {
  components: IStandComponent[];
}
interface IStandConfigurations {
  [name: string]: IStandConfiguration;
}

export const STAND_PAGES_NAMES = {
  dashboard: { label: $localize`Online Stand`, path: null, icon: 'stand:featured_play_list', rel: '' },
  news: { label: $localize`News`, path: 'news', icon: 'stand:newspaper', rel: 'nofollow' },
  products: { label: $localize`Products`, path: 'products', icon: 'stand:collections', rel: 'nofollow' },
};

@Injectable({
  providedIn: 'root'
})
export class StandService {

  public get standPages(): IStandPage[] {
    return Object.values(STAND_PAGES_NAMES)
    .filter(p => {
      switch (p.path) {
        case 'news':
          return this.currentCompany.newsCount > 0 || this.usersService.isInRole([UserRolesEnum.admin, UserRolesEnum.stand_admin]);
          break;
        case 'products':
          return this.currentCompany.productsCount > 0 || this.usersService.isInRole([UserRolesEnum.admin, UserRolesEnum.stand_admin]);
          break;
        default:
          return true;
      }
    })
    .map(p => p);
  }

  // private $onChangeHeader: ReplaySubject<any>;

  get standLayout() {
    return this._standLayout.asReadonly();
  }
  private _standLayout = signal<IStandLayoutOptions>({ collapse: false, animate: true, hideNavBar: false, lockCollapsed: false });

  private $onDestroyStand: Subject<boolean>;
  private $currentCompany: BehaviorSubject<ICompany>;
  public $onSlideStand: Subject<any>;
  public $onHeaderStateChange: Subject<boolean>;
  public $onDescriptionExpanded: Subject<boolean>;
  private currentCompany: ICompany;


  private standServiceurations: IStandConfigurations;
  private lastPageData: any;
  private currentPageName: string;
  private currentStandPageUrl: string;
  private currentQueryString: string;

  public currentCategoriesPath: string;
  public currentCategory = signal<ICategory>(null);


  public canonicalDefaultUrl: IPathLocale;
  public canonicalUrl: IPathLocale;
  public breadcrumbs: IBreadcrumb[] = [];

  private history: HistoryEntry[] = [];
  private currentHistoryIndex = 0;
  private destroyed = false;

  constructor(
    private router: Router,
    private companyBySlugGQL: CompanyBySlugGQL,
    private _platform: Platform,
    private routesService: RoutesService,
    private languagesService: LanguagesService,
    @Optional() private metadataService: MetadataService,
    private breadcrumbsService: BreadcrumbsService,
    private usersService: UsersService,
    private location: Location,
    private errorService: ErrorService,
    @Inject(LOCALE_ID) private locale: string,
    private countriesService: IsoCountriesService
  ) {

    // this.$onChangeHeader = new ReplaySubject();

    this.$onDestroyStand = new Subject();
    // this.$openStandVideo = new Subject();

    this.$onSlideStand = new Subject();
    this.$onHeaderStateChange = new Subject();
    this.$onDescriptionExpanded = new Subject();
    const standRegex = new RegExp(/\/(stand|stand-free)\//);


    const mainStream = this.$onDestroyStand
    .pipe(
      tap((destroyed) => this.destroyed = destroyed),
      filter(destroyed => destroyed === false),
      startWith(false)
    );

    const routerStream = this.router.events
    .pipe(
      filter(e => e instanceof NavigationStart ||  e instanceof NavigationEnd)
    );

    this.router.events
    .subscribe((e) => {
      if(e instanceof Scroll) {
        if ((e.position  && e.position[1] > 0) || false) {
          this.$onHeaderStateChange.next(true);
        } else {
          this.$onHeaderStateChange.next(false);
        }
      }

      if(e instanceof RouteConfigLoadStart && !isNaN(e.route.data?.layout)) {
          this.setLayout(e.route.data.layout);
      }

      if(e instanceof ActivationEnd && !isNaN(e.snapshot.data?.layout)) {
        this.setLayout(e.snapshot.data.layout);
      }
    });
    // Per ovviare al problema che per questo servizio non avviene mai l'onDestroy (lazy loaded Module)
    // https://stackoverflow.com/questions/53222529/services-lifecycle-destroy-a-service
    // https://stackoverflow.com/questions/38950435/rxjs-resubscribe-to-unsubscribed-observable

    mainStream
    .pipe(
      mergeMap(
        () => routerStream.pipe(
          scan<NavigationStart | NavigationEnd, RouterHistory>(
            (acc, event) => {
              if (event instanceof NavigationStart) {
                // We need to track the trigger, id, and idToRestore from the NavigationStart events
                return {
                  ...acc,
                  event,
                  trigger: event.navigationTrigger,
                  id: event.id,
                  idToRestore:
                    (event.restoredState && event.restoredState.navigationId) ||
                    undefined
                };
              }
              // NavigationEnd events
              const history = [...acc.history];
              let currentIndex = acc.currentIndex;
              // browser events (back/forward) are popstate events
              if (acc.trigger === 'popstate') {
                // get the history item that references the idToRestore
                const idx = history.findIndex(x => x.id === acc.idToRestore);
                // if found, set the current index to that history item and update the id
                if (idx > -1) {
                  currentIndex = idx;
                  history[idx].id = acc.id;
                } else {
                  currentIndex = 0;
                }
              } else {
                // remove all events in history that come after the current index
                history.splice(currentIndex + 1);
                // add the new event to the end of the history and set that as our current index
                history.push({ id: acc.id, url: event.urlAfterRedirects });
                currentIndex = history.length - 1;
              }
              return {
                ...acc,
                event,
                history,
                currentIndex
              };
            },
            {
              event: null,
              history: [],
              trigger: null,
              id: 0,
              idToRestore: 0,
              currentIndex: 0
            }
          ),
          // filter out so we only act when navigation is done
          filter(
            ({ event, trigger }) => event instanceof NavigationEnd
          ),
          takeUntil(this.$onDestroyStand)
        )
      )
    )
    .subscribe(({ history, currentIndex }) => {
      this.history = history;
      this.currentHistoryIndex = currentIndex;
    });


    mainStream
    .pipe(
      mergeMap(() =>
        routerStream
        .pipe(
          filter(e => e instanceof NavigationEnd),
          takeUntil(this.$onDestroyStand)
        )
      )
    ).subscribe((e: NavigationEnd) => {
      if (!this.currentCompany) {
        return;
      }
      const url = e.urlAfterRedirects;
      // uscita dallo stand component;
      if (!standRegex.test(url)){
        this.destroy();
      }

      let route = this.router.routerState.root;

      while (route.firstChild) {
        route = route.firstChild;
      }
      let curPage;
      const urlParts = url.split(/[?#]/);
      curPage = urlParts[0].split('/').pop();

      if (this.platform.isBrowser && curPage === this.currentCompany.slug ) {
        const locState: any = this.location.getState();
        locState.stand = 'next';
        this.location.replaceState(url, '', locState);
      }

      this.currentStandPageUrl = curPage === this.currentCompany.slug ? '' : curPage;
      this.currentQueryString = urlParts.length > 1 ? urlParts[1] : undefined;
      this.canonicalUrl = { lang: this.currentLanguageCode, path: url };
      if (this.currentCategoriesPath !== this.currentCompany.primaryPath) {
        const normalized = this.currentCategoriesPath.replace('/', '\/');
        const re = new RegExp(`^\/hall${normalized}\/`);
        const replacingPath = `/hall${this.currentCompany.primaryPath}/`;
        this.canonicalUrl.path = url.replace(re, replacingPath);
      }
      this.canonicalDefaultUrl = { ...this.canonicalUrl };

      route.data.subscribe(data => {
        const urlSegments = url.split('/');
        urlSegments.shift();
        const bc: IBreadcrumb[] = [];
        let i = urlSegments.length - 1;
        if(!urlSegments.includes(this.currentCompany.slug)){
          return;
        }

        this.currentPageName = '';
        while (urlSegments[i] !== this.currentCompany.slug) {
          let label: string;
          switch (urlSegments[i]) {
            case 'news':
              label = this.currentPageName = STAND_PAGES_NAMES.news.label;
              this.canonicalDefaultUrl.path = this.canonicalDefaultUrl.path.replace('/news', '');
              break;
            case 'products':
              label = this.currentPageName = STAND_PAGES_NAMES.products.label;
              this.canonicalDefaultUrl.path = this.canonicalDefaultUrl.path.replace('/products', '');
              break;
            default:
              if (data.product) {
                label = data.product.name;
              } else if (data.news) {
                label = data.news.title;
              }
          }
          bc.splice(0, 0, {label: label, url: `/${urlSegments.slice(0, i + 1).join('/')}`});
          i--;
        }
        this.breadcrumbsService.store(this.breadcrumbs.concat(bc));
      });
    });

    if (this.platform.isBrowser) {
      mainStream
      .pipe(
        mergeMap(() => fromEvent(window, 'popstate', { passive: true, capture: true })
        .pipe(
          takeUntil(this.$onDestroyStand)
        ))
      )
      .subscribe((event: any) => {
        if (event.state?.stand) {
          const nextHistoryIndex = this.history.findIndex(i => i.url === window.location.pathname);
          const _state = this.location.getState();
          _state[`stand`] = this.currentHistoryIndex > nextHistoryIndex ? 'prev' : 'next';
          this.$onSlideStand.next(true);
        }
      });
    }
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    if (this.$currentCompany) {
      this.$currentCompany.complete();
    }

    // da realizzare meglio
    if (state.url.indexOf('/edit/info/') > -1) {
      return;
    }
    this.$currentCompany = new BehaviorSubject(null);
    const category = `/${route.params.category}` + (route.params.subcategory ? `/${route.params.subcategory}` : '');
    const slug = route.params.companySlug;

    return this.companyBySlugGQL.fetch({ 
      slug: slug, 
      lang: this.currentLanguageCode, 
      category: category 
    }, {
      // fetchPolicy: 'no-cache'
      fetchPolicy: 'cache-first'
    })
      .pipe(
        map(result => {
          // initialize variables
          if(this.destroyed) {
            this.$onDestroyStand.next(false);
          }

          this.currentCategoriesPath = null;
          this.canonicalUrl = null;
          this.breadcrumbs = [];

          let company = result.data.companyBySlug;
          if (!company) {
            throw new PageNotFoundError(state.url);
          } 
          else if((state.url.indexOf('/stand-free/') > -1) && (company.accountType == AccountType.premium || company.accountType == AccountType.customer)){
            throw new PageNotFoundError(state.url);
          } 
          else if((state.url.indexOf('/stand/') > -1) && (company.accountType == AccountType.freeMember || company.accountType == AccountType.basicMember)){
            throw new PageNotFoundError(state.url);
          } 
          else {
            if (this.currentCompany && this.currentCompany.slug !== company.slug) {
              this.setCustomReuseStrategy();
            }
          }
          this.usersService.setCurrentCompanyRole(company._id);

          this.currentCategoriesPath = `/${route.params.category}/${(route.params.subcategory || '')}`;
          const currentCategory = company.categories
          .filter(c => c.level < 2)
          .find(cat => cat.path.startsWith(this.currentCategoriesPath));
          const needRedirect = !currentCategory || slug !== company.slug;
          if (needRedirect) {
            // some category name or company slug had changed
            //  redirect 301
            this.router.navigate([this.routesService.getCompanyUrl(company)]);
          }

          // breadcrumbs
          this.breadcrumbs.push({ label: currentCategory.parent.shortName, url: `/hall${currentCategory.parent.path}` });
          if (route.params.subcategory) {
            this.breadcrumbs.push({ label: currentCategory.shortName, url: `/hall${currentCategory.path}` });
          }
          if((company.accountType == AccountType.premium || company.accountType == AccountType.customer))
          {
            this.breadcrumbs.push({ label: company.fullName, url: `${this.breadcrumbs[this.breadcrumbs.length - 1].url}/stand/${company.slug}` });
          }
          else{
            this.breadcrumbs.push({ label: company.fullName, url: `${this.breadcrumbs[this.breadcrumbs.length - 1].url}/stand-free/${company.slug}` });
          }
          this.breadcrumbsService.store(this.breadcrumbs);

          company = {
            ...company,
            primaryAddress: company.addresses.find(a => a.primary === true)
          };
          this.currentCategory.set(currentCategory);
          this.currentCompany = company;
          this.$currentCompany.next(company);
          return { company: company };
        }),
        catchError((err) => {
          return from(this.errorService.handleError(err));
        })

      );
  }

  destroy(): void {
    this.breadcrumbs = [];
    this.$onDestroyStand.next(true);
  }
  getCurrentCompany(): Observable<ICompany> {
    return this.$currentCompany.asObservable();
  }
  getConfiguration(configurationName: string): IStandConfiguration {
    return this.standServiceurations[configurationName];
  }

  getResolvedUrl(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .map(v => v.url.map(segment => segment.toString()).join('/'))
      .join('/');
  }

  get isFirstPage(): boolean {
    return this.lastPageData === undefined;
  }

  get platform(): Platform {
    return this._platform;
  }

  get currentLanguageCode(): string {
    return this.languagesService.getCurrentLangCode();
  }

  setLayout(layout: StandLayoutEnum): void {
    let options: IStandLayoutOptions;
    const defaultCfg = { collapse: false, animate: true, hideNavBar: false, lockCollapsed: false };
    switch (layout) {
      case StandLayoutEnum.complete:
        options = defaultCfg;
        break;
      case StandLayoutEnum.compact:
        options = { collapse: true, animate: !this.isFirstPage, hideNavBar: !this.isFirstPage, lockCollapsed: true };
        break;
      case StandLayoutEnum.admin:
        options = { collapse: true, animate: !this.isFirstPage, hideNavBar: true, showBackBtn: true, lockCollapsed: true };
        break;
      case StandLayoutEnum.detailsPage:
        options = { collapse: false, animate: !this.isFirstPage, hideNavBar: false, lockCollapsed: false, customH1Tag: true };
        break;
        case StandLayoutEnum.standFree:
          options = defaultCfg;
          break;
    }

    this._standLayout.set(options);
    // this.$onChangeHeader.next(options);
  }

  appendCompanyMetaTags(canonical: IPathLocale, carousel?: string[], index?: RobotsIndexEnum): void {
    const company = this.currentCompany;
    let alternativeLangs: IPathLocale[] = [];

    alternativeLangs = company.primaryPathLocales
      .map(pathLocale => {
        const _url = this.routesService.getCompanyAltLangUrl(company, pathLocale.lang);
        const urlTree = this.router.parseUrl(_url);

        urlTree.queryParams = this.router.routerState.snapshot.root.queryParams;

        return {
          lang: pathLocale.lang,
          // path: (this.currentStandPageUrl ? `${_url}/${this.currentStandPageUrl}` : _url) + this.currentQueryString ? `?${this.currentQueryString }` : ''
          path: (this.currentStandPageUrl ? `${_url}/${this.currentStandPageUrl}` : _url)
        } as IPathLocale;
      });

    const { city, regionCode, countryCode } = company.addresses[0];
    const country = this.countriesService.getName(countryCode, this.locale);
    const companyTitleTpl = `${company.fullName} ${$localize `in `}${city} ${regionCode ? `(${regionCode})`: ''} - ${country}`;
    
    let _title
    if (company.metaTitle && company.metaTitle != '') {
     _title = company.metaTitle;
    } else {
     _title = this.currentPageName ? `${this.currentPageName} - ${companyTitleTpl}` : `${companyTitleTpl}`;
    }
    const _description = company.shortDescription ? company.shortDescription :
    $localize`Information and contacts of ${company.fullName}, a company specializing in ${this.currentCategory().shortName}. Learn about products, services, news, and submit your inquiry!`;

    // aggiungo Meta tags lato server
    this.metadataService.appendMetadata({
      title: _title,
      description: _description,
      images: [company.companyImage],
      pageType: 'company',
      company: company,
      categories: company.categories.map(c => c.refPath)
    },
    canonical,
    alternativeLangs, index, company, carousel);
  }

  navigateNextStand(): void {
    const nextIndex = this.history.findIndex(i => i.url.endsWith(this.currentCompany.nextCompany.slug));
    const nextCompany = this.currentCompany.nextCompany;
    this.$onSlideStand.next(true);
    if (nextIndex > -1) {
      this.location.historyGo(nextIndex - this.currentHistoryIndex);
      return;
    }
    const url = this.routesService.getCompanyUrl(nextCompany, this.currentCategoriesPath);
    setTimeout(() => {
      this.router.navigate([url], {
        state: { stand: 'next' }
      });
    }, 10);
  }

  navigatePreviousStand(): void {
    if (this.currentCompany.prevCompany) {
      const regex = new RegExp(`.*\/${this.currentCompany.prevCompany.slug}(\/.*)?`);
      const previousIndex = this.history.findIndex(i => regex.test(i.url));
      const relativeIndex = -(this.currentHistoryIndex - previousIndex);
      this.$onSlideStand.next(true);
      this.location.historyGo(relativeIndex);
    }
  }

  get hasHistory(): boolean {
    if (! this.currentCompany.prevCompany) {
      return false;
    }
    const regex = new RegExp(`.*\/${this.currentCompany.prevCompany.slug}(\/.*)?`);
    return this.history.findIndex(i => regex.test(i.url)) > -1;
  }

  setCustomReuseStrategy(): void{
    const currShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute;

    this.router.routeReuseStrategy.shouldReuseRoute = (future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean => {
      if (curr.data.shouldReuseOnSlide === false || future.data.shouldReuseOnSlide === false) {
        return false;
      }
      return true;
    }

    this.router.events
    .pipe(
      filter((e) => e instanceof NavigationEnd),
      take(1)
    ).subscribe(() => this.router.routeReuseStrategy.shouldReuseRoute = currShouldReuseRoute);
  }

}
